import React, { useState, useRef, useEffect } from "react";
// Import the separate CSS module for CBSE Class 10
import styles from "./CBSEClassSyllabus.module.css";
// Assuming these components exist in your project structure
import Chatbot from '../../components/chatbot';
import SocialSidebar from "../../components/SocialSideBar";

// --- Data for the CBSE Class 10 Syllabus ---
const syllabusData = {
  mathematics: [
    {
      chapter: "Number Systems",
      content: `
        • Real numbers — Euclid’s division lemma, Fundamental Theorem of Arithmetic  
        • Revisiting irrational numbers, their decimal expansions  
        • Operations on real numbers, laws of exponents for real numbers`,
      marks: "10 (approx)",
      notes: `
        • No major deletions reported.  
        • Emphasis on clarity of irrational numbers and real line.`
    },
    {
      chapter: "Algebra",
      content: `
        • Polynomials: zeros, relationship between zeros & coefficients  
        • Pair of linear equations in two variables — graphical & algebraic methods  
        • Quadratic equations: solutions, discriminant, applications`,
      marks: "20",
      notes: `
        • Some higher techniques (e.g. cross-multiplication) may not be expected.  
        • Focus on standard methods: factorization, quadratic formula.`
    },
    {
      chapter: "Coordinate Geometry",
      content: `
        • Coordinate geometry: distance formula, section formula  
        • Graph of linear equations`,
      marks: "4",
      notes: `
        • Simple internal division only; more complex locus problems are avoided.`
    },
    {
      chapter: "Geometry",
      content: `
        • Triangles: similarity, properties, basic theorems  
        • Circles: tangents, properties of tangents  
        • Constructions: construction of tangents, division of angles, etc.`,
      marks: "27",
      notes: `
        • Some advanced constructions or proofs may be omitted.  
        • Focus on classical geometry.`
    },
    {
      chapter: "Mensuration",
      content: `
        • Areas related to circles, surface areas & volumes  
        • Problems on combinations of solids`,
      marks: "13",
      notes: `
        • Complex shapes / composite solids may be restricted.  
        • Frustum of a cone, if included, only basic cases.`
    },
    {
      chapter: "Statistics & Probability",
      content: `
        • Statistics: mean, median, mode of grouped data, cumulative frequency curves  
        • Probability: classical definition, simple problems`,
      marks: "6",
      notes: `
        • No advanced probability distributions.  
        • Cumulative frequency / graphical representations included.`
    }
  ],

  science: [
    {
      chapter: "Chemical Reactions and Equations",
      content: `
        • Types of chemical reactions: combination, decomposition, displacement, double displacement, precipitation, neutralization, oxidation & reduction  
        • Balancing chemical equations`,
      marks: "9",
      notes: `
        • Full chapter retained.  
        • Focus on concept and practice.`
    },
    {
      chapter: "Acids, Bases & Salts",
      content: `
        • Definition (H⁺ and OH⁻ concepts), properties, examples & uses  
        • pH scale, importance in daily life  
        • Preparation & uses: NaOH, bleaching powder, baking soda, washing soda, Plaster of Paris`,
      marks: "9",
      notes: `
        • The logarithmic definition of pH is **not required**.  
        • Emphasis on real-life applications.`
    },
    {
      chapter: "Metals & Non-Metals",
      content: `
        • Properties, reactivity series, ionic compounds formation  
        • Metallurgy basics, corrosion & prevention`,
      marks: "9",
      notes: `
        • Some advanced metallurgy steps may be simplified.`
    },
    {
      chapter: "Carbon & its Compounds",
      content: `
        • Covalent bonding, versatile nature of carbon  
        • Homologous series, nomenclature (halogens, alcohols, ketones, aldehydes, alkanes, alkynes)  
        • Reactions: combustion, oxidation, addition, substitution; properties & uses  
        • Soaps and detergents`,
      marks: "9",
      notes: `
        • Complex mechanisms are minimized.  
        • Focus on simpler reaction types and nomenclature.`
    },
    {
      chapter: "Life Processes",
      content: `
        • Nutrition, respiration, transport, excretion in plants & animals`,
      marks: "12",
      notes: `
        • Full chapter.  
        • Basic concepts and diagrams.`
    },
    {
      chapter: "Control & Co-ordination",
      content: `
        • Plant movements, plant hormones  
        • Animal nervous system, endocrine system, reflex actions`,
      marks: "12",
      notes: `
        • Focus on functional understanding rather than biochemistry depth.`
    },
    {
      chapter: "How do Organisms Reproduce?",
      content: `
        • Asexual & sexual reproduction in plants & animals  
        • Reproductive health, methods of contraception, HIV/AIDS`,
      marks: "12",
      notes: `
        • Emphasis on awareness and basic biology.`
    },
    {
      chapter: "Heredity & Evolution",
      content: `
        • Mendel’s laws (monohybrid)  
        • Sex determination  
        • **Evolution portion is removed / not required**`,
      marks: "12",
      notes: `
        • Evolution theory is **deleted** in many CBSE published syllabi.  
        • Focus on heredity and simple genetics.`
    },
    {
      chapter: "Light: Reflection & Refraction",
      content: `
        • Reflection from curved surfaces, mirror formula, magnification  
        • Refraction, lens formula, power of lens`,
      marks: "13",
      notes: `
        • Derivations are limited.  
        • Only principal formulas expected.`
    },
    {
      chapter: "Human Eye & Colourful World",
      content: `
        • Structure & function of the eye, defects & their correction  
        • Dispersion, scattering of light, applications in daily life`,
      marks: "13",
      notes: `
        • Some advanced optics (e.g. prism experiments) included.`
    },
    {
      chapter: "Electricity",
      content: `
        • Ohm’s law, current, voltage, resistance, series & parallel circuits  
        • Heating effect of current, power & interrelation among P, V, I, R`,
      marks: "13",
      notes: `
        • Core electrical concepts.  
        • Avoid advanced circuit networks beyond basics.`
    },
    {
      chapter: "Magnetic Effects of Electric Current",
      content: `
        • Magnetic field, Biot-Savart, Ampere’s law, force on conductor, electric motor  
        • Electromagnetic induction, generator, domestic circuits`,
      marks: "13",
      notes: `
        • Some parts (e.g. DC generator detailed parts) may be reduced.  
        • Focus on conceptual clarity.`
    },
    {
      chapter: "Our Environment",
      content: `
        • Ecosystem, environmental issues, ozone depletion, waste management, biodegradable vs non-biodegradable`,
      marks: "5",
      notes: `
        • Simple and conceptually important.`
    }
  ],

  social_studies: [
    {
      chapter: "History: India & the Contemporary World — II",
      content: `
        • The Rise of Nationalism in Europe  
        • Nationalism in India  
        • Print culture and the modern world (selected topics)  
        • (The Making of a Global World & Age of Industrialisation are for internal / project only)`,
      marks: "20",
      notes: `
        • Only specified subtopics in “Making of a Global World” are to be evaluated.  
        • Some chapters are internal assessment only.`
    },
    {
      chapter: "Geography: Contemporary India — II",
      content: `
        • Resource & development  
        • Forest & wildlife resources  
        • Water resources  
        • Agriculture  
        • Manufacturing industries  
        • Lifelines of national economy`,
      marks: "20",
      notes: `
        • Some topics (e.g. Minerals & Energy Resources) are assessed in map work / internal only.  
        • Emphasis on map skills and application.`
    },
    {
      chapter: "Political Science: Democratic Politics — II",
      content: `
        • Power sharing  
        • Federalism  
        • Gender, religion & caste  
        • Political parties  
        • Outcomes of democracy`,
      marks: "20",
      notes: `
        • Some chapters like “Democracy & Diversity”, “Challenges to Democracy” are **deleted**.  
        • Stick to the core chapters.`
    },
    {
      chapter: "Economics: Understanding Economic Development",
      content: `
        • Development, Income & other goals  
        • Sectors of Indian economy  
        • Money & credit  
        • Globalisation & Indian economy`,
      marks: "20",
      notes: `
        • “Consumer Rights” is often relegated to internal assessment.  
        • Depth is limited in globalisation part.`
    }
  ],

  english: [
    {
      chapter: "Reading Skills",
      content: `
        • Discursive passage (~400–450 words)  
        • Case-based factual passage (~200–250 words)  
        • Questions: MCQ, short answers, inference`,
      marks: "20",
      notes: `
        • Two unseen passages. Total reading ~600–700 words.  
        • Note-making / summarizing may be removed in some versions.`
    },
    {
      chapter: "Writing Skills & Grammar",
      content: `
        • Writing: Formal letter, analytical paragraph, article, etc.  
        • Grammar: Tenses, Modals, Subject-Verb Concord, Reported Speech, Determiners`,
      marks: "10 (grammar) + 10 (writing)",
      notes: `
        • Active/Passive and Clauses are **deleted** in many syllabi.  
        • Writing tasks limited to formal / analytical types only.`
    },
    {
      chapter: "Literature — (First Flight)",
      content: `
        • Prose & poetry chapters as per First Flight textbook  
        • Questions: short, long, extract based`,
      marks: "40",
      notes: `
        • All chapters are generally retained.  
        • Poems and prose both included.`
    },
    {
      chapter: "Literature — (Footprints Without Feet)",
      content: `
        • Prose / stories from supplementary reader  
        • Questions: long, short, extract-based`,
      marks: "40",
      notes: `
        • Entire set of short stories / chapters are usually included.`
    }
  ],

  hindi: [
    {
      chapter: "अपठित गद्यांश / काव्यांश",
      content: `
        • Two unseen passages (prose / poetry)  
        • MCQ / subjective questions based on them`,
      marks: "10",
      notes: `
        • Objective type questions.`
    },
    {
      chapter: "व्यावहारिक व्याकरण",
      content: `
        • Topics: वाक्य भेद, वाच्य, पद परिचय, रस, अलंकार  
        • MCQ format`,
      marks: "20",
      notes: `
        • Only limited grammar topics.  
        • No deep theoretical grammar required.`
    },
    {
      chapter: "क्षितिज भाग 2 (Prose & Poetry)",
      content: `
        • Comprehension, short/long answers from prescribed chapters`,
      marks: "35",
      notes: `
        • Mix of objective & subjective questions.`
    },
    {
      chapter: "कृतिका भाग 2 (Supplement)",
      content: `
        • Questions from prescribed texts (long answers)`,
      marks: "15",
      notes: `
        • Emphasis on themes, characters, literary devices.`
    },
    {
      chapter: "लेखन कौशल",
      content: `
        • Article, formal / informal letters, advertisement, message writing`,
      marks: "20",
      notes: `
        • Creative / formal writing only.  
        • No story writing or other types often deleted.`
    }
  ]
};


const SyllabusTable = ({ data }) => (
  <div className={styles['table-container']}>
    <table className={styles['syllabus-table']}>
      <thead>
        <tr>
          <th>Chapter Name</th>
          <th>Syllabus Content</th>
          <th>Approx. Marks*</th>
          <th>Notes / Status</th>
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={index}>
            <td>{item.chapter}</td>
            <td>{item.content}</td>
            <td className={styles['marks-column']}>{item.marks}</td>
            <td>{item.notes}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className={styles['table-footer']}>*Marks are approximate unit-wise weightage for theory paper (80 marks) and may vary slightly.</p>
  </div>
);

const CBSEClass10Syllabus = () => {
  const [activeTab, setActiveTab] = React.useState("science");
  const [indicatorStyle, setIndicatorStyle] = React.useState({});
  const tabsRef = React.useRef(null);

  const tabs = [
    { id: "science", label: "Science 🔬" },
    { id: "mathematics", label: "Mathematics 📐" },
    { id: "social_studies", label: "Social Studies 🌍" },
    { id: "english", label: "English 📚" },
    { id: "hindi", label: "Hindi 🇮🇳" },
  ];

  // Logic for the sliding underline indicator
  React.useEffect(() => {
    const activeTabIndex = tabs.findIndex((tab) => tab.id === activeTab);
    const activeTabElement = tabsRef.current?.children[activeTabIndex];

    if (activeTabElement) {
      setIndicatorStyle({
        left: activeTabElement.offsetLeft,
        width: activeTabElement.offsetWidth,
      });
    }
  }, [activeTab]);

  return (
    <div className={styles['syllabus-container']}>
      <Chatbot /> 
      <SocialSidebar />
      <header className={styles['syllabus-header']}>
        <h1>
          <span role="img" aria-label="Syllabus icon">🎓</span>
          <span className={styles['gradient-text']}>
            Class 10 Syllabus
          </span>
        </h1>
        <p>Subject-wise breakdown and Chapter Status for the current academic session.</p>
      </header>

      <div className={styles['syllabus-tabs-container']}>
        <div className={styles['syllabus-tabs']} ref={tabsRef}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
          <span className={styles['tab-indicator']} style={indicatorStyle} />
        </div>
      </div>

      <SyllabusTable data={syllabusData[activeTab]} />
    </div>
  );
};

export default CBSEClass10Syllabus;